import React, { useCallback, useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import type { WaveformStatus } from './useAudioPeaks'

interface AudioWaveformProps {
  peaks: number[] | null
  status: WaveformStatus
  /** Playback position, 0..1. The played part is drawn at full strength. */
  progress: number
  /** Called with a 0..1 position when the waveform is clicked. */
  onSeek?: (fraction: number) => void
  className?: string
}

const BAR_WIDTH = 2
const BAR_GAP = 1
const UNPLAYED_ALPHA = 0.35

/**
 * Whole-file waveform drawn on a canvas sized to its box. Colour comes from the
 * element's own `color` (a theme token class), so it follows light/dark mode.
 * Without peaks (still decoding, or decoding failed) it shows a flat line.
 */
export function AudioWaveform({ peaks, status, progress, onSeek, className }: AudioWaveformProps) {
  const boxRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [size, setSize] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const box = boxRef.current
    if (!box) return
    const measure = () => setSize({ width: box.clientWidth, height: box.clientHeight })
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(box)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !peaks || size.width === 0 || size.height === 0) return
    const ratio = window.devicePixelRatio || 1
    canvas.width = Math.round(size.width * ratio)
    canvas.height = Math.round(size.height * ratio)
    const context = canvas.getContext('2d')
    if (!context) return

    context.setTransform(ratio, 0, 0, ratio, 0, 0)
    context.clearRect(0, 0, size.width, size.height)
    context.fillStyle = getComputedStyle(canvas).color

    const bars = Math.max(1, Math.floor(size.width / (BAR_WIDTH + BAR_GAP)))
    const middle = size.height / 2
    for (let bar = 0; bar < bars; bar++) {
      const peak = peaks[Math.min(peaks.length - 1, Math.floor((bar / bars) * peaks.length))] ?? 0
      const height = Math.max(1, peak * (size.height - 2))
      const x = bar * (BAR_WIDTH + BAR_GAP)
      context.globalAlpha = x / size.width < progress ? 1 : UNPLAYED_ALPHA
      context.fillRect(x, middle - height / 2, BAR_WIDTH, height)
    }
    context.globalAlpha = 1
  }, [peaks, size, progress])

  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!onSeek) return
    e.stopPropagation()
    const rect = e.currentTarget.getBoundingClientRect()
    if (rect.width === 0) return
    onSeek(Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)))
  }, [onSeek])

  return (
    <div
      ref={boxRef}
      className={cn('nodrag relative w-full cursor-pointer text-primary', className)}
      data-waveform={status}
      onClick={handleClick}
      role="img"
      aria-label={status === 'failed' ? 'Waveform unavailable' : 'Audio waveform'}
    >
      {peaks ? (
        <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
      ) : (
        <div
          className={cn(
            'absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-muted-foreground/40',
            status === 'loading' && 'animate-pulse',
          )}
        />
      )}
    </div>
  )
}
